import { DATE_FORMAT } from "@/constants/datetime";
import { ERSOContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { validator } from "@/utils/validator";
import { MAX_CHARACTER_INPUT_VALIDATION, MAX_TEXT_INPUT_VALIDATION } from "@/constants/common";

export const schema = validator.object().shape({
  id: validator.number().nullable().label("組織ID（自動生成）").notRequired(),
  name: validator.string().trim().emptyToNull().max(60).label("登録支援機関名").required(),
  licenseNumber: validator
    .string()
    .trim()
    .emptyToNull()
    .label("登録番号")
    .notRequired()
    .matches(/^\d{2}登-\d{6}$/, { message: "無効な登録番号の形式です。期待される形式: YY登-XXXXXX", excludeEmptyString: true }),
  contractStatus: validator
    .string()
    .isSelection()
    .emptyToNull()
    .label("ステータス")
    .test((value, ctx) => {
      const todayStr = dayjs().format(DATE_FORMAT);
      const today = dayjs(todayStr);
      const trialEndDateStr = ctx.parent.trialEndDate ? dayjs(ctx.parent.trialEndDate).format(DATE_FORMAT) : null;
      const contractEndDateStr = ctx.parent.contractEndDate ? dayjs(ctx.parent.contractEndDate).format(DATE_FORMAT) : null;
      const contractStartDateStr = ctx.parent.contractStartDate ? dayjs(ctx.parent.contractStartDate).format(DATE_FORMAT) : null;

      // Validation for TRIAL status
      if (
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ERSOContractStatus.TRIAL &&
        trialEndDateStr &&
        today.isAfter(dayjs(trialEndDateStr))
      ) {
        return ctx.createError({ message: "トライアル終了日が過ぎているため、ステータスを「トライアル中」に設定することはできません。" });
      }

      // Validation for DURING_CONTRACT status: can only set if:
      // - No trial dates (trialStartDate and trialEndDate are both null/undefined)
      // - OR today >= trialEndDate
      if (ctx.parent.contractStatus && ctx.parent.contractStatus === ERSOContractStatus.DURING_CONTRACT) {
        const hasTrialStartDate = ctx.parent.trialStartDate !== null && ctx.parent.trialStartDate !== undefined;
        const hasTrialEndDate = ctx.parent.trialEndDate !== null && ctx.parent.trialEndDate !== undefined;

        // If both trial dates are missing, allow DURING_CONTRACT
        if (!hasTrialStartDate && !hasTrialEndDate) {
          // Allow: no trial dates
        } else if (hasTrialEndDate && trialEndDateStr) {
          // If trialEndDate exists, today must be >= trialEndDate
          if (today.isBefore(dayjs(trialEndDateStr))) {
            return ctx.createError({
              message:
                "トライアル終了日が未来の日付の場合、ステータスを「契約中」に設定することはできません。トライアル終了日以降に設定してください。",
            });
          }
        } else {
          // If trialStartDate exists but trialEndDate doesn't, don't allow
          if (hasTrialStartDate && !hasTrialEndDate) {
            return ctx.createError({ message: "トライアル開始日がある場合、トライアル終了日も必要です。" });
          }
        }
      }

      // Validation for TRIAL and DURING_CONTRACT: cannot set if contractEndDate < today
      if (
        ctx.parent.contractStatus &&
        [ERSOContractStatus.TRIAL, ERSOContractStatus.DURING_CONTRACT].includes(ctx.parent.contractStatus) &&
        contractEndDateStr &&
        today.isAfter(dayjs(contractEndDateStr))
      ) {
        return ctx.createError({ message: "解約日を過ぎているため、ステータスを「トライアル中」または「契約中」に設定することはできません。" });
      }

      if (
        !ctx.parent.trialEndDate &&
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ERSOContractStatus.TRIAL &&
        contractStartDateStr &&
        today.isSameOrAfter(dayjs(contractStartDateStr))
      ) {
        return ctx.createError({ message: "契約開始日が本日または過去の日付の場合、「トライアル」ステータスは選択できません。" });
      }

      if (
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ERSOContractStatus.TRIAL_CANCELLED &&
        contractStartDateStr &&
        dayjs(contractStartDateStr).isSameOrBefore(today)
      ) {
        return ctx.createError({ message: "契約開始日が本日または過去の日付の場合、「トライアル解約済み」ステータスは選択できません。" });
      }

      return true;
    })
    .required(),
  contractPlan: validator.string().trim().emptyToNull().label("契約プラン").max(MAX_CHARACTER_INPUT_VALIDATION).notRequired(),
  trialStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("トライアル開始日")
    .isBeforeDate("trialEndDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .notRequired(),
  trialEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .label("トライアル終了日")
    .notRequired(),
  contractStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("契約日")
    .isSameOrAfterDate("trialEndDate", { startLabel: "トライアル終了日", endLabel: "契約日" })
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "契約日" })
    .when("contractStatus", {
      is: ERSOContractStatus.DURING_CONTRACT,
      then: (schema) => schema.required(),
    })
    .notRequired(),
  contractEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .isAfterDate("contractStartDate", { startLabel: "契約日", endLabel: "解約日" })
    .label("解約日")
    .notRequired(),
  webAccessEnabled: validator.boolean().label("学習アプリをWebから利用可能にする").default(false),
  description: validator.string().trim().emptyToNull().label("顧客情報").notRequired().max(MAX_TEXT_INPUT_VALIDATION),
  note: validator.string().trim().emptyToNull().label("備考").notRequired().max(MAX_TEXT_INPUT_VALIDATION),
  prefecture: validator.string().trim().emptyToNull().label("都道府県").notRequired(),
});
